import React, { useState } from 'react';
import { Activity } from 'lucide-react';
import { LeftPanel, MatchState } from './components/LeftPanel';
import { RightPanel, ResultsData } from './components/RightPanel';
export function App() {
  const [matchState, setMatchState] = useState<MatchState>({
    teamA: 'Arsenal',
    teamB: 'Chelsea',
    oddsA: '2.10',
    oddsB: '3.40',
    isHomeA: true,
    formA: ['G', 'G', 'E', 'G', 'P'],
    formB: ['P', 'E', 'G', 'P', 'P'],
    injuryA: 'low',
    injuryB: 'med',
    homeWinRateA: '70',
    awayWinRateA: '45',
    homeWinRateB: '55',
    awayWinRateB: '40',
    avgGoalsScoredA: '2.1',
    avgGoalsConcededA: '0.8',
    avgGoalsScoredB: '1.4',
    avgGoalsConcededB: '1.2',
    motivationA: 'high',
    motivationB: 'med',
    suspensionsA: 'none',
    suspensionsB: 'minor'
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState<ResultsData | null>(null);
  const handleAnalyze = () => {
    setIsAnalyzing(true);
    // Simulate API call / complex calculation
    setTimeout(() => {
      // Mock logic based on inputs to make it feel dynamic
      const oddsANum = parseFloat(matchState.oddsA) || 2.0;
      const oddsBNum = parseFloat(matchState.oddsB) || 2.0;
      // Calculate implied probabilities from odds
      const impliedProbA = Math.round(1 / oddsANum * 100);
      const impliedProbB = Math.round(1 / oddsBNum * 100);
      // Base probability calculation (mock)
      let probA = impliedProbA;
      let probB = impliedProbB;
      // Adjust based on form
      const formScoreA = matchState.formA.filter(r => r === 'G').length * 5;
      const formScoreB = matchState.formB.filter(r => r === 'G').length * 5;
      probA += formScoreA;
      probB += formScoreB;
      // Adjust based on home/away
      if (matchState.isHomeA) {
        probA += (parseFloat(matchState.homeWinRateA) || 50) * 0.1;
        probB += (parseFloat(matchState.awayWinRateB) || 50) * 0.1;
      } else {
        probA += (parseFloat(matchState.awayWinRateA) || 50) * 0.1;
        probB += (parseFloat(matchState.homeWinRateB) || 50) * 0.1;
      }
      // Adjust based on motivation
      if (matchState.motivationA === 'high') probA += 5;
      if (matchState.motivationA === 'low') probA -= 5;
      if (matchState.motivationB === 'high') probB += 5;
      if (matchState.motivationB === 'low') probB -= 5;
      // Adjust based on suspensions
      if (matchState.suspensionsA === 'critical') probA -= 8;
      if (matchState.suspensionsA === 'minor') probA -= 3;
      if (matchState.suspensionsB === 'critical') probB -= 8;
      if (matchState.suspensionsB === 'minor') probB -= 3;
      // Normalize
      const total = probA + probB + 20; // 20% base draw
      const finalProbA = Math.round(probA / total * 100);
      const finalProbB = Math.round(probB / total * 100);
      const drawProb = 100 - finalProbA - finalProbB;
      // Value calculation mock
      const edgeA = finalProbA - impliedProbA;
      const edgeB = finalProbB - impliedProbB;
      const maxEdge = Math.max(edgeA, edgeB);
      const valueInd = Math.min(100, Math.max(0, Math.round(50 + maxEdge * 2 + formScoreA)));
      let rec: 'high' | 'medium' | 'avoid' = 'avoid';
      if (valueInd > 75 && maxEdge > 5) rec = 'high';else if (valueInd > 40 && maxEdge > 0) rec = 'medium';
      setResults({
        winProbA: finalProbA,
        winProbB: finalProbB,
        drawProb: drawProb,
        impliedProbA: impliedProbA,
        impliedProbB: impliedProbB,
        adjScoreA: Math.round(finalProbA * 1.2),
        adjScoreB: Math.round(finalProbB * 1.1),
        valueIndicator: valueInd,
        recommendation: rec
      });
      setIsAnalyzing(false);
    }, 1500);
  };
  return <div className="min-h-screen bg-[#0a0e1a] text-slate-200 font-sans selection:bg-emerald-500/30 overflow-x-hidden relative" data-id="element-0">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none" data-id="element-1">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-blue-900/10 blur-[120px]" data-id="element-2" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-emerald-900/10 blur-[120px]" data-id="element-3" />
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.015] mix-blend-overlay" data-id="element-4" />
      </div>

      {/* Header */}
      <header className="relative z-10 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-md sticky top-0" data-id="element-5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between" data-id="element-6">
          <div className="flex items-center gap-3" data-id="element-7">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20" data-id="element-8">
              <Activity className="w-5 h-5 text-white" data-id="element-9" />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-white" data-id="element-10">
              BetIQ Pro
            </h1>
            <span className="ml-3 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold uppercase tracking-wider" data-id="element-11">
              Motor de Inteligencia v2.4
            </span>
          </div>
          <div className="flex items-center gap-4 text-sm font-medium text-slate-400" data-id="element-12">
            <span className="flex items-center gap-2" data-id="element-13">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse-slow" data-id="element-14" />
              Sistema en Línea
            </span>
            <div className="w-px h-4 bg-slate-700" data-id="element-15" />
            <button className="hover:text-white transition-colors" data-id="element-16">
              Documentación
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8" data-id="element-17">
        <div className="grid grid-cols-1 lg:grid-cols-[45%_1fr] gap-8 items-start" data-id="element-18">
          <LeftPanel state={matchState} setState={setMatchState} onAnalyze={handleAnalyze} isAnalyzing={isAnalyzing} data-id="element-19" />
          <div className="sticky top-24" data-id="element-20">
            <RightPanel results={results} teamA={matchState.teamA} teamB={matchState.teamB} data-id="element-21" />
          </div>
        </div>
      </main>
    </div>;
}